// Advanced Performance Monitoring & Optimization
// Industry-grade performance tracking

export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  
  private constructor() {}
  
  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  // Track Core Web Vitals
  trackWebVitals() {
    if (typeof window === 'undefined') return;

    // Largest Contentful Paint
    new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        console.log('LCP:', entry.startTime);
        // Send to analytics if needed
        if (window.posthog) {
          window.posthog.capture('web_vital_lcp', {
            value: entry.startTime,
            rating: entry.startTime > 2500 ? 'poor' : entry.startTime > 1200 ? 'needs_improvement' : 'good'
          });
        }
      }
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay
    new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        const firstInputEntry = entry as any; // Cast to handle processingStart property
        const fidValue = firstInputEntry.processingStart - firstInputEntry.startTime;
        console.log('FID:', fidValue);
        if (window.posthog) {
          window.posthog.capture('web_vital_fid', {
            value: fidValue,
            rating: fidValue > 300 ? 'poor' : fidValue > 100 ? 'needs_improvement' : 'good'
          });
        }
      }
    }).observe({ entryTypes: ['first-input'] });

    // Cumulative Layout Shift
    let clsValue = 0;
    new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        const layoutShiftEntry = entry as any; // Cast to handle hadRecentInput property
        if (!layoutShiftEntry.hadRecentInput) {
          clsValue += layoutShiftEntry.value;
        }
      }
      console.log('CLS:', clsValue);
      if (window.posthog) {
        window.posthog.capture('web_vital_cls', {
          value: clsValue,
          rating: clsValue > 0.25 ? 'poor' : clsValue > 0.1 ? 'needs_improvement' : 'good'
        });
      }
    }).observe({ entryTypes: ['layout-shift'] });
  }

  // Track resource loading times
  trackResourceTiming() {
    if (typeof window === 'undefined') return;

    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      const paint = performance.getEntriesByType('paint');
      
      const metrics = {
        // Page Load Metrics
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
        loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
        
        // Paint Metrics
        firstPaint: paint.find(p => p.name === 'first-paint')?.startTime || 0,
        firstContentfulPaint: paint.find(p => p.name === 'first-contentful-paint')?.startTime || 0,
        
        // Network Metrics
        dnsLookup: navigation.domainLookupEnd - navigation.domainLookupStart,
        tcpConnection: navigation.connectEnd - navigation.connectStart,
        serverResponse: navigation.responseEnd - navigation.requestStart,
      };

      console.log('Performance Metrics:', metrics);
      
      if (window.posthog) {
        window.posthog.capture('page_performance', metrics);
      }
    });
  }

  // Preload critical resources
  preloadCriticalResources() {
    if (typeof document === 'undefined') return;

    // next/font and next/image manage their own preload tags. Only warm up
    // third-party hosts that can be used after consent.
    this.dnsPrefetch(['us.i.posthog.com', 'fonts.googleapis.com']);
  }

  private dnsPrefetch(domains: string[]) {
    domains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = `//${domain}`;
      document.head.appendChild(link);
    });
  }

  // Lazy load non-critical resources
  lazyLoadResources() {
    if (typeof window === 'undefined') return;

    // Lazy load images with intersection observer
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          img.src = img.dataset.src!;
          img.classList.remove('lazy');
          observer.unobserve(img);
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
}

// Initialize performance monitoring
export const performanceMonitor = PerformanceMonitor.getInstance();
